import { useState } from 'react';
import { Avatar } from '@/components/Avatar';
import { BuscadorDePersonas } from '@/components/BuscadorDePersonas';
import { EstadoError } from '@/components/EstadoError';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Drawer } from '@/ui/Drawer';
import { Skeleton } from '@/ui/Skeleton';
import {
  useAgregarMiembro, useCambiarRol, useMiembros, useQuitarMiembro,
  type Comunidad, type Miembro,
} from './api';

/**
 * Miembros.tsx — quién está dentro y con qué papel. Módulo 9, opción 4.
 *
 * ## Lo que estaba mal, y ahora no
 *
 *   · **El buscador no funcionaba con teclado.** Los resultados eran
 *     `<div onclick>` con `onmouseover`/`onmouseout` escribiendo
 *     `style.background` a mano: sin foco, sin Enter, mudos para un lector de
 *     pantalla. Ahora es `BuscadorDePersonas`, el mismo de Reconocimientos y
 *     Celebraciones.
 *   · **Ofrecía a gente que ya estaba dentro**, y el servidor contestaba un 400
 *     —«El usuario ya es miembro»— después de pulsar. Ahora no salen.
 *   · **Quitar a alguien y cambiarle el rol no preguntaban nada**, y ninguna de
 *     las dos se deshace sola. Quitar pregunta; cambiar el rol no, porque se
 *     revierte pulsando otra vez.
 *   · **Las iniciales del avatar daban 2,52 de contraste**: `--orange-text` —la
 *     tinta para escribir sobre *superficies*— encima de `--brand`. El endpoint
 *     ya devolvía `avatar_url` y el vanilla no lo usaba; ahora se pinta la foto,
 *     y quien no tenga cae en `Avatar`, cuyos colores ya están medidos.
 *   · **El id de la comunidad viajaba por `window._commMembersId`**, que
 *     `Convenciones.md` prohíbe por nombre. Ahora es una prop.
 */
export function Miembros({
  abierto, comunidad, onCerrar,
}: {
  abierto: boolean;
  comunidad: Comunidad;
  onCerrar: () => void;
}) {
  const { data, isLoading, isError, error, refetch } = useMiembros(abierto ? comunidad.id : null);
  const agregar = useAgregarMiembro();
  const cambiarRol = useCambiarRol();
  const quitar = useQuitarMiembro();
  const [quitando, setQuitando] = useState<Miembro | null>(null);

  const miembros = data ?? [];
  const moderadores = miembros.filter((m) => m.role === 'moderator').length;

  const aviso = (err: unknown, porDefecto: string) =>
    Utils.toast(err instanceof Error ? err.message : porDefecto, 'error');

  return (
    <Drawer
      abierto={abierto}
      onCambio={(v) => { if (!v) { setQuitando(null); onCerrar(); } }}
      titulo="Miembros"
      descripcion={comunidad.name}
      pie={<Button variant="ghost" onClick={onCerrar}>Cerrar</Button>}
    >
      <div className="flex flex-col gap-4">
        <div>
          <h3 className="mb-1.5 text-2 font-semibold text-ink-2">Agregar a alguien</h3>
          <BuscadorDePersonas
            id="cm-buscar-persona"
            etiqueta="Buscar una persona para agregar a la comunidad"
            marcador="Buscar persona para agregar…"
            excluir={miembros.map((m) => m.user_id)}
            alElegir={(p) =>
              agregar.mutate(
                { comunidadId: comunidad.id, userId: p.id },
                {
                  onSuccess: () => Utils.toast(`${p.full_name} se agregó a la comunidad`, 'success'),
                  onError: (err) => aviso(err, 'No se pudo agregar'),
                },
              )
            }
          />
        </div>

        <div>
          <h3 className="mb-1.5 text-2 font-semibold text-ink-2" id="cm-lista-miembros">
            {isLoading ? 'Cargando…' : `${miembros.length} ${miembros.length === 1 ? 'persona' : 'personas'}`}
          </h3>

          {isLoading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : isError ? (
            <EstadoError err={error} que="los miembros" onReintentar={() => void refetch()} />
          ) : !miembros.length ? (
            <p className="text-3 text-muted">
              Sin miembros todavía. Busca a alguien arriba para empezar.
            </p>
          ) : (
            <ul aria-labelledby="cm-lista-miembros" className="flex flex-col gap-2">
              {miembros.map((m) => (
                <li
                  key={m.user_id}
                  className="flex items-center gap-3 rounded-sm border border-line p-2.5"
                >
                  <Avatar nombre={m.full_name} foto={m.avatar_url} tamano={36} />
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-1.5 text-3 font-semibold text-ink">
                      <span className="truncate">{m.full_name}</span>
                      {m.role === 'moderator' && <Badge tone="info">Moderador</Badge>}
                    </p>
                    {m.position && <p className="truncate text-1 text-muted">{m.position}</p>}
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    loading={
                      cambiarRol.isPending && cambiarRol.variables?.userId === m.user_id
                    }
                    onClick={() =>
                      cambiarRol.mutate(
                        {
                          comunidadId: comunidad.id,
                          userId: m.user_id,
                          rol: m.role === 'moderator' ? 'member' : 'moderator',
                        },
                        {
                          onSuccess: () =>
                            Utils.toast(
                              m.role === 'moderator'
                                ? `${m.full_name} ya no modera la comunidad`
                                : `${m.full_name} ahora modera la comunidad`,
                              'success',
                            ),
                          onError: (err) => aviso(err, 'No se pudo cambiar el rol'),
                        },
                      )
                    }
                  >
                    {/* Sin color propio: hereda el del botón. `--color-accent`
                        sobre una superficie clara da menos de 3, y el estado ya
                        lo dicen la estrella llena o vacía y el texto de al lado. */}
                    <i
                      className={`bi ${m.role === 'moderator' ? 'bi-star-fill' : 'bi-star'}`}
                      aria-hidden="true"
                    />
                    {m.role === 'moderator' ? 'Quitar moderador' : 'Hacer moderador'}
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setQuitando(m)}
                    aria-label={`Quitar a ${m.full_name} de la comunidad`}
                  >
                    <i className="bi bi-x-lg text-danger-ink" aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Un aviso, no un bloqueo: el servidor no impide dejar la comunidad sin
            moderador, así que decirlo es lo honesto. Bloquearlo aquí daría una
            regla que solo existe en la pantalla. */}
        {!isLoading && !isError && miembros.length > 0 && moderadores === 0 && (
          <p className="flex items-start gap-2 rounded-sm border border-line bg-sunken p-2.5 text-2 text-ink-2">
            <i className="bi bi-exclamation-triangle mt-0.5 shrink-0 text-warn-ink" aria-hidden="true" />
            Esta comunidad no tiene ningún moderador. Sin moderador, nadie de dentro puede
            gestionar miembros ni publicar comunicados.
          </p>
        )}
      </div>

      {/* Sobre un panel ya abierto: por eso `Utils.preguntar` existe en el
          vanilla y por eso aquí va un diálogo propio y no `Utils.confirm`. */}
      <ConfirmarQuitar
        m={quitando}
        cargando={quitar.isPending}
        onCancelar={() => setQuitando(null)}
        onConfirmar={() => {
          if (!quitando) return;
          quitar.mutate(
            { comunidadId: comunidad.id, userId: quitando.user_id },
            {
              onSuccess: () => {
                Utils.toast(`${quitando.full_name} ya no es miembro`, 'info');
                setQuitando(null);
              },
              onError: (err) => {
                aviso(err, 'No se pudo quitar');
                setQuitando(null);
              },
            },
          );
        }}
      />
    </Drawer>
  );
}

function ConfirmarQuitar({
  m, cargando, onCancelar, onConfirmar,
}: {
  m: Miembro | null;
  cargando: boolean;
  onCancelar: () => void;
  onConfirmar: () => void;
}) {
  if (!m) return null;
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="cm-quitar-titulo"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-sm rounded-md border border-line bg-raised p-4 shadow-lg">
        <h2 id="cm-quitar-titulo" className="text-4 font-bold text-brand-ink">
          Quitar a {m.full_name}
        </h2>
        <p className="mt-1.5 text-3 text-ink-2">
          Dejará de ver el muro de la comunidad. Puedes volver a agregarlo cuando quieras.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancelar}>Cancelar</Button>
          <Button variant="destructive" loading={cargando} onClick={onConfirmar}>
            Quitar
          </Button>
        </div>
      </div>
    </div>
  );
}
